import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { Badge, DemoBadge, ErrorMsg, Loading, Sq, StatusMark, StudioLogo, Tabs, Time } from '../components/ui';
import { api, errorText, newIdempotencyKey } from '../lib/api';
import { COMPLETENESS_LABEL, countryName, INTEREST_LABEL, jobType, TERMINAL } from '../lib/format';
import { useApi, useSession } from '../lib/session';
import type { Evidence, Job, MergedStub, StudioDetail } from '../lib/types';
import { DemoteDialog, PromoteDialog } from './studio/DeepDialogs';
import { ChangesTab, PositioningTab, SocialTab } from './studio/DeepTabs';
import { NotesTab, PricingTab, ServicesTab, SourcesTab } from './studio/OtherTabs';
import { OverviewTab } from './studio/OverviewTab';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'positioning', label: 'Positioning' },
  { id: 'services', label: 'Projects & Services' },
  { id: 'social', label: 'Social Activity' },
  { id: 'pricing', label: 'Pricing' },
  { id: 'changes', label: 'Changes' },
  { id: 'sources', label: 'Sources' },
  { id: 'notes', label: 'Notes' },
] as const;
type TabId = (typeof TABS)[number]['id'];

function isMerged(x: StudioDetail | MergedStub | null): x is MergedStub {
  return !!x && 'merged_into' in x;
}

export function StudioPage() {
  const { id } = useParams();
  const { user } = useSession();
  const [sp, setSp] = useSearchParams();
  const tab = (TABS.find((t) => t.id === sp.get('tab'))?.id ?? 'overview') as TabId;
  const detail = useApi<StudioDetail | MergedStub>(`/api/studios/${id}`);
  const evidence = useApi<Evidence>(`/api/studios/${id}/evidence`);
  const jobs = useApi<{ items: Job[] }>(`/api/jobs?studio_id=${id}&limit=30`);
  const [dialog, setDialog] = useState<'promote' | 'demote' | null>(null);
  const [notice, setNotice] = useState<{ msg: string; tone: 'ok' | 'warn' } | null>(null);
  const [actionErr, setActionErr] = useState('');
  const [refreshJob, setRefreshJob] = useState<Job | null>(null);
  const [refreshBusy, setRefreshBusy] = useState(false);
  const pollRef = useRef<number | null>(null);

  const reloadAll = () => {
    detail.reload();
    evidence.reload();
    jobs.reload();
  };
  const notify = (msg: string, tone: 'ok' | 'warn' = 'ok') => setNotice({ msg, tone });

  // A different studio in the same component: start clean.
  useEffect(() => {
    setRefreshJob(null);
    setNotice(null);
    setActionErr('');
    setDialog(null);
  }, [id]);

  // Poll a refresh job until it finishes.
  const refreshId = refreshJob?.id;
  const refreshDone = refreshJob ? TERMINAL.includes(refreshJob.status) : true;
  useEffect(() => {
    if (!refreshId || refreshDone) return;
    const tick = async () => {
      try {
        const j = await api<Job>(`/api/jobs/${refreshId}`);
        setRefreshJob(j);
        if (TERMINAL.includes(j.status)) {
          detail.reload();
          evidence.reload();
          jobs.reload();
        }
      } catch {
        /* keep polling; a transient failure is shown by the next success */
      }
    };
    pollRef.current = window.setInterval(tick, 2000);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshId, refreshDone]);

  if (detail.loading && !detail.data) return <Loading what="Loading the studio" />;
  if (!detail.data) return <ErrorMsg>{detail.error || 'Studio not found.'}</ErrorMsg>;
  if (isMerged(detail.data)) {
    return (
      <div className="page narrow">
        <h1>{detail.data.name}</h1>
        <p className="lede">
          This record was merged into another studio. Nothing was deleted: its sources, facts and notes now belong to the surviving record.
        </p>
        {detail.data.merged_into ? (
          <Link className="btn primary" to={`/competitors/${detail.data.merged_into}`}>
            Open the surviving record
          </Link>
        ) : (
          <p className="muted">The surviving record could not be resolved.</p>
        )}
      </div>
    );
  }
  const s = detail.data;
  const website = s.website_url || (s.domain ? `https://${s.domain}` : null);

  // Refresh and Check info are both website jobs: one is followed at a time.
  async function startJob(path: 'refresh' | 'check-info', what: string) {
    setRefreshBusy(true);
    setActionErr('');
    try {
      const r = await api<{ job_id: string; status: string; created: boolean }>(`/api/studios/${s.id}/${path}`, { method: 'POST', idempotencyKey: newIdempotencyKey() });
      const j = await api<Job>(`/api/jobs/${r.job_id}`);
      setRefreshJob(j);
      if (!r.created) notify(`${what} for this studio was already queued or running; following that job.`, 'warn');
    } catch (e) {
      setActionErr(errorText(e));
    } finally {
      setRefreshBusy(false);
    }
  }

  const refresh = () => startJob('refresh', 'A refresh');
  const checkInfo = () => startJob('check-info', 'A check');

  async function setInterest(interest: string) {
    setActionErr('');
    try {
      await api('/api/studios/bulk', { method: 'POST', body: { ids: [s.id], interest } });
      notify(`Interest set to ${INTEREST_LABEL[interest]}.`);
      detail.reload();
    } catch (e) {
      setActionErr(errorText(e));
    }
  }

  async function setWatched(watched: boolean) {
    setActionErr('');
    try {
      await api(`/api/studios/${s.id}/watchlist`, { method: 'PUT', body: { watched } });
      notify(watched ? 'Added to the watch list. Nothing new is collected because of it.' : 'Removed from the watch list.');
      detail.reload();
    } catch (e) {
      setActionErr(errorText(e));
    }
  }

  const canRefresh = !!website && !s.is_demo;

  return (
    <div className="page wide">
      <p className="crumbs small">
        <Link to="/competitors">Competitors</Link> / {s.name}
      </p>
      <header className="studio-head">
        <div className="studio-title">
          <h1 className="with-logo">
            <StudioLogo src={s.logo_url} size="large" />
            {s.name}
          </h1>
          <p className="studio-meta">
            {website ? (
              <a href={website} target="_blank" rel="noreferrer noopener">
                {s.domain ?? website}
              </a>
            ) : (
              <span className="unknown">no website</span>
            )}
            <span className="sep">·</span>
            <Sq /> {s.city ?? 'city unknown'}, {countryName(s.country)}
            {s.offices.length > 1 && <span className="muted"> (+{s.offices.length - 1} other offices)</span>}
          </p>
          <p className="badges">
            {s.research_depth === 'deep' ? <Badge tone="deep">Deep</Badge> : <Badge>Light</Badge>}
            <span className={`interest i-${s.interest}`}>{INTEREST_LABEL[s.interest]}</span>
            <Badge tone={s.completeness === 'complete' ? 'ok' : undefined}>{COMPLETENESS_LABEL[s.completeness]} profile</Badge>
            {s.watchlist && <Badge tone="deep">On watch list</Badge>}
            {s.is_demo && <DemoBadge />}
            {s.proposed_facts > 0 && <Badge tone="warn">{s.proposed_facts} proposed</Badge>}
          </p>
          {s.is_demo && <p className="demo-notice small">Fictional demo studio on a reserved .example domain. Not a real company; nothing can be collected from it.</p>}
        </div>
        <div className="studio-actions">
          {s.research_depth === 'light' ? (
            <button type="button" className="btn primary" onClick={() => setDialog('promote')}>
              Promote to Deep
            </button>
          ) : (
            <>
              <button type="button" className="btn" onClick={() => setDialog('promote')}>
                Deep settings
              </button>
              <button type="button" className="btn ghost" onClick={() => setDialog('demote')}>
                Demote to Light
              </button>
            </>
          )}
          <button
            type="button"
            className="btn"
            onClick={() => void refresh()}
            disabled={!canRefresh || refreshBusy || (!!refreshJob && !refreshDone)}
            title={s.is_demo ? 'Demo studios are fictional: there is no website to collect' : !website ? 'No website recorded' : 'Read the website again (Light refresh)'}
          >
            {refreshJob && !refreshDone && refreshJob.type !== 'check_info' ? 'Refreshing…' : 'Refresh from website'}
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => void checkInfo()}
            disabled={!canRefresh || refreshBusy || (!!refreshJob && !refreshDone)}
            title={s.is_demo ? 'Demo studios are fictional: there is no website to check' : !website ? 'No website recorded' : 'Look for missing information (mostly social accounts) on the studio’s own website'}
          >
            {refreshJob && !refreshDone && refreshJob.type === 'check_info' ? 'Checking…' : 'Check info'}
          </button>
          <button
            type="button"
            className={s.watchlist ? 'btn' : 'btn ghost'}
            aria-pressed={s.watchlist}
            data-testid="watch-toggle"
            onClick={() => void setWatched(!s.watchlist)}
            title={s.watchlist ? 'On the watch list (Watch list page). Click to remove.' : 'Add to the watch list: its main figures on one page'}
          >
            {s.watchlist ? 'Watching' : 'Watch'}
          </button>
          <label className="select-filter">
            <span>Interest</span>
            <select value={s.interest} onChange={(e) => void setInterest(e.target.value)}>
              {Object.entries(INTEREST_LABEL).map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          {!canRefresh && <p className="muted small">{s.is_demo ? 'Refresh is off for demo studios.' : 'No website to refresh from.'}</p>}
        </div>
      </header>

      {refreshJob && (
        <p className={`msg ${refreshJob.status === 'failed' ? 'err' : TERMINAL.includes(refreshJob.status) ? 'ok' : 'info'}`} role="status">
          <StatusMark status={refreshJob.status} /> {jobType(refreshJob.type)}: {refreshJob.status.replace('_', ' ')}
          {refreshJob.progress && typeof refreshJob.progress.page === 'number' && !refreshDone && (
            <>
              {' '}
              · page {String(refreshJob.progress.page)} of {String(refreshJob.progress.of)}
            </>
          )}
          {refreshJob.error && <> · {refreshJob.error}</>} · <Link to={`/jobs/${refreshJob.id}`}>job details</Link>
          {refreshDone && <> · finished <Time iso={refreshJob.finished_at} /></>}
        </p>
      )}
      {notice && (
        <p className={`msg ${notice.tone}`} role="status">
          {notice.msg}{' '}
          <button type="button" className="link small" onClick={() => setNotice(null)}>
            dismiss
          </button>
        </p>
      )}
      <ErrorMsg>{actionErr}</ErrorMsg>

      <Tabs
        tabs={TABS.map((t) => ({ id: t.id, label: t.id === 'overview' && s.proposed_facts > 0 ? `${t.label} (${s.proposed_facts})` : t.label }))}
        value={tab}
        onChange={(t) => {
          const n = new URLSearchParams(sp);
          if (t === 'overview') n.delete('tab');
          else n.set('tab', t);
          setSp(n, { replace: true });
        }}
      />
      <div className="tab-body" role="tabpanel">
        {tab === 'overview' && <OverviewTab
            studio={s}
            evidence={evidence.data}
            notify={notify}
            reload={reloadAll}
            jobs={jobs.data?.items ?? null}
            checking={!!refreshJob && !refreshDone && refreshJob.type === 'check_info'}
            onCheck={canRefresh ? () => void checkInfo() : null}
          />}
        {tab === 'positioning' && <PositioningTab studio={s} evidence={evidence.data} canReview={user?.role === 'owner'} />}
        {tab === 'services' && <ServicesTab studio={s} evidence={evidence.data} />}
        {tab === 'social' && <SocialTab studio={s} canReview={user?.role === 'owner'} />}
        {tab === 'pricing' && <PricingTab studio={s} evidence={evidence.data} />}
        {tab === 'changes' && <ChangesTab studio={s} jobs={jobs.data?.items ?? null} />}
        {tab === 'sources' && <SourcesTab evidence={evidence.data} loading={evidence.loading} error={evidence.error} />}
        {tab === 'notes' && <NotesTab studio={s} onAdded={(m) => (notify(m), detail.reload())} />}
      </div>

      {dialog === 'promote' && (
        <PromoteDialog
          studio={s}
          onClose={() => setDialog(null)}
          onDone={(m) => {
            setDialog(null);
            notify(m);
            reloadAll();
          }}
        />
      )}
      {dialog === 'demote' && (
        <DemoteDialog
          studio={s}
          onClose={() => setDialog(null)}
          onDone={(m) => {
            setDialog(null);
            notify(m);
            reloadAll();
          }}
        />
      )}
    </div>
  );
}
